// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import type { Message, ThreadTask, ChatCheckpoint } from '@/types';
import { haveSameMessageShape } from '@utils/transcriptMessageSemantics';

export type ThreadViewSource =
  | 'empty'
  | 'persisted-cache'
  | 'history'
  | 'runtime-projection'
  | 'ui-action';

export interface ThreadRuntimeView {
  threadId: string;
  sessionId?: string;
  messages: Message[];
  tasks: ThreadTask[];
  checkpoints: ChatCheckpoint[];
  historicalMessageIds: string[];
  version: number;
  source: ThreadViewSource;
  loadedAt?: number;
  loading: {
    history: boolean;
    tasks: boolean;
    checkpoints: boolean;
    replay: boolean;
  };
  error: string | null;
}

export function createEmptyThreadView(threadId: string): ThreadRuntimeView {
  return {
    threadId,
    messages: [],
    tasks: [],
    checkpoints: [],
    historicalMessageIds: [],
    version: 0,
    source: 'empty',
    loading: {
      history: false,
      tasks: false,
      checkpoints: false,
      replay: false,
    },
    error: null,
  };
}

export function applyHistoryLoadedToThreadView(
  view: ThreadRuntimeView,
  params: {
    sessionId: string;
    threadId: string;
    messages: Message[];
  }
): ThreadRuntimeView {
  if (view.threadId !== params.threadId) {
    return view;
  }
  return {
    ...view,
    sessionId: params.sessionId,
    messages: params.messages,
    historicalMessageIds: params.messages.map((m) => m.id),
    version: view.version + 1,
    source: 'history',
    loadedAt: Date.now(),
    loading: { ...view.loading, history: false },
  };
}

export function applyRuntimeProjectionToThreadView(
  view: ThreadRuntimeView,
  params: {
    sessionId: string;
    threadId: string;
    messages: Message[];
  }
): ThreadRuntimeView {
  if (view.threadId !== params.threadId) {
    return view;
  }

  // History is higher authority than runtime projection.
  // Do not let sparse runtime overwrite richer history.
  if (
    view.source === 'history' &&
    params.messages.length < view.messages.length
  ) {
    return view;
  }

  // 幂等守卫（2026-10-26 实测修复：打开任一已有会话后 100% CPU 空转）：
  // 投影调用方 `syncRuntimeMessages` 在每次 set 后都会重算 store 状态，
  // 若这里无条件 `version: view.version + 1`，即便正文完全没变也会产生新的
  // `currentThreadView` → `selectCurrentTranscriptMessages` 返回新数组 →
  // `mappedMessages` 新引用 → 同步 effect 再次 set → 无限循环
  // （React 报 `Maximum update depth exceeded`，实测约 980 commits/秒、
  //  localStorage 每秒写入上千次，页面 DOM 完全不变）。
  // 内容与归属都没变时返回同一个 view 引用，让 set 成为空操作。
  const sameMessages =
    view.messages === params.messages ||
    haveSameThreadViewMessages(view.messages, params.messages);
  if (
    sameMessages &&
    view.sessionId === params.sessionId &&
    view.source === 'runtime-projection'
  ) {
    return view;
  }

  return {
    ...view,
    sessionId: params.sessionId,
    messages: params.messages,
    version: view.version + 1,
    source: 'runtime-projection',
  };
}

/**
 * 投影幂等比较：以**真实语义相等**判定「没有新内容」，避免把同一批消息反复写成
 * 新 view 造成渲染自喂（打开已有会话后 100% 主线程空转的根因之一）。
 *
 * 2026-10-27 独立验收 P1：早期实现只比 `content` 与
 * `thinking_trace.steps.length`/`ui_blocks.length`，把「同数量内的真实变化」误判为
 * 无变化 —— 同一 plot_card 参数 v0 10→20、同一步骤 running→completed、
 * `action_revision` 1→2 都被拦下，导致 session 缓存新、当前权威视图旧的
 * 内容不一致。这里改用 `@utils/transcriptMessageSemantics` 的逐字段值比较
 * （与 sync 投影守卫同一事实源），既保留幂等收益，也不吞掉真实更新。
 */
function haveSameThreadViewMessages(left: readonly Message[], right: readonly Message[]): boolean {
  return haveSameMessageShape(left as Message[], right as Message[]);
}

export function applyUiActionPatchToThreadView(
  view: ThreadRuntimeView,
  params: {
    threadId: string;
    targetMessageId: string;
    patchedMessages: Message[];
  }
): ThreadRuntimeView {
  if (view.threadId !== params.threadId) {
    return view;
  }

  const targetIndex = view.messages.findIndex(
    (m) => m.id === params.targetMessageId
  );
  if (targetIndex === -1) {
    return view;
  }

  return {
    ...view,
    messages: params.patchedMessages,
    version: view.version + 1,
    source: 'ui-action',
  };
}

/**
 * Upsert a single locally-created message (e.g. a DeepAgent bridge result
 * summary) into a thread view. If a message with the same id is already
 * present, it is replaced so a richer late-arriving DeepAgent result can
 * upgrade an earlier placeholder/default completion entry.
 */
export function appendLocalMessageToThreadView(
  view: ThreadRuntimeView,
  params: {
    threadId: string;
    message: Message;
  }
): ThreadRuntimeView {
  if (view.threadId !== params.threadId) {
    return view;
  }
  const existingIndex = view.messages.findIndex((m) => m.id === params.message.id);
  if (existingIndex >= 0) {
    const nextMessages = [...view.messages];
    nextMessages[existingIndex] = params.message;
    return {
      ...view,
      messages: nextMessages,
      version: view.version + 1,
      source: 'ui-action',
    };
  }
  return {
    ...view,
    messages: [...view.messages, params.message],
    version: view.version + 1,
    source: 'ui-action',
  };
}

export function applyThreadSwitchToThreadView(
  view: ThreadRuntimeView,
  params: {
    threadId: string;
    sessionId: string;
  }
): ThreadRuntimeView {
  if (
    view.threadId === params.threadId &&
    view.sessionId === params.sessionId
  ) {
    return view;
  }
  return { ...createEmptyThreadView(params.threadId), sessionId: params.sessionId };
}

export function clearThreadView(view: ThreadRuntimeView): ThreadRuntimeView {
  return createEmptyThreadView(view.threadId);
}
